import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {connect} from "react-redux";
import { Switch } from '@headlessui/react'
import Loading from "../../common/Loading";
import * as api from "../../../api";
import {resultBoolean, convertBoolean} from "../../common/Global";
import {CModal} from "@coreui/react";
import UserPointModal from "./UserPointModal";
import UserMoneyModal from "./UserMoneyModal";
import UserMessageModal from "./UserMessageModal";
import numeral from "numeral";
import { isGameEnabled } from "../../../config/menuConfig";

// localStorage에서 사용자 정보 읽기
const getStorageWithExpiry = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const parsedItem = JSON.parse(item);
    // 만료 시간이 설정되어 있고, 현재 시간이 만료 시간을 초과한 경우
    if (parsedItem.expiry && new Date().getTime() > parsedItem.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return parsedItem.value;
  } catch (error) {
    console.error('localStorage 읽기 오류:', error);
    localStorage.removeItem(key);
    return null;
  }
};

const EmptyCell = ({ span = 1 }) => (
    <div className={`col-span-${span}`}/>
);

// 헤더-값 쌍을 표시하는 공통 컴포넌트 - col-span-2로 수정
const InfoItem = ({ label, children, bgColor = "bg-gray-50" }) => {
  return (
    <>
      <div className={`${bgColor} p-2 flex items-center col-span-1`}>
        <span className={"text-sm font-bold"}>{label}</span>
      </div>
      <div className="p-2 flex items-center col-span-1">
        {children}
      </div>
    </>
  );
};

// 텍스트 값 표시용 컴포넌트
const TextValue = ({ value, isBold = false }) => (
  <span className={`text-sm ${isBold ? "font-bold" : ""}`}>{value}</span>
);

// 숫자 포맷팅 컴포넌트
const NumberValue = ({ value, isBold = false, color = "" }) => (
  <span className={`text-sm ${isBold ? "font-bold" : ""} ${color}`}>{numeral(value).format("0,0")} 원</span>
);

// 입력 필드 컴포넌트
const InputField = ({ name, value, onChange, type = "text" }) => (
  <input 
    name={name} 
    className={"w-full rounded border px-2 py-1 text-sm"}
    type={type} 
    value={value} 
    onChange={onChange}
  />
);

// 스위치 토글 컴포넌트
const ToggleSwitch = ({ value, onChange, useResultBoolean = false }) => (
  <Switch
    onChange={value => onChange(value)}
    checked={useResultBoolean ? resultBoolean(value) : convertBoolean(value)}
    className="group relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 data-[checked]:bg-indigo-600"
  >
    <span
      aria-hidden="true"
      className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
    />
  </Switch>
);

// 공통으로 사용되는 RollingRateInput 컴포넌트
const RollingRateInput = ({ item, onChange }) => {
    return (
        <div className={"border rounded p-2"}>
            <div className={"bg-gray-100 p-2 mb-2 font-bold text-sm"}>{item.gameNm}</div>
            <div className={"flex flex-col gap-2"}>
                <div className={"flex flex-col gap-1"}>
                    <div className={"flex flex-row items-center gap-1"}>
                        <input 
                            className={"w-[50px] border px-1 text-sm"}
                            type="text"
                            value={item.rollingRate}
                            onChange={(e) => onChange(e.target.value, item)}
                        />
                        <span className={"text-sm text-blue-500"}>% (상위수수료율 : {item.recommenderRollingRate})</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// 게임 섹션 컴포넌트
const GameSection = ({ title, gameDiv, defaultRate, setDefaultRate, userInfo, setUserInfo }) => {
    const handleRateChange = (inputValue, item) => {
        const updatedList = userInfo.userRollingList.map(listItem => 
            (listItem.gameDiv === item.gameDiv && listItem.gameType === item.gameType) 
                ? { ...listItem, rollingRate: inputValue } 
                : listItem
        );
        
        if (inputValue === '' || isNaN(inputValue)) {
            setUserInfo({
                ...userInfo,
                userRollingList: updatedList
            });
            return;
        }

        const numValue = parseFloat(inputValue);
        
        if (numValue > 100) {
            window.alert('100%를 초과할 수 없습니다.');
            return;
        }
        
        if (item.isTopUser === 'N') {
            if (numValue <= item.recommenderRollingRate) {
                setUserInfo({
                    ...userInfo,
                    userRollingList: updatedList
                });
            } else {
                window.alert(`상위수수료율(${item.recommenderRollingRate}%)을 초과할 수 없습니다.`);
            }
        } else {
            setUserInfo({
                ...userInfo,
                userRollingList: updatedList
            });
        }
    };

    const applyDefaultRate = () => {
        if (defaultRate === '' || isNaN(defaultRate)) return;
        
        const numValue = parseFloat(defaultRate);
        if (numValue > 100) {
            window.alert('100%를 초과할 수 없습니다.');
            return;
        }

        const updatedList = [...userInfo.userRollingList];
        userInfo.userRollingList.forEach((item, idx) => {
            if (item.gameDiv === gameDiv) {
                if (item.isTopUser === 'N') {
                    if (numValue <= item.recommenderRollingRate) {
                        updatedList[idx] = { ...item, rollingRate: defaultRate };
                    } else {
                        window.alert(`${item.gameNm}의 상위수수료율(${item.recommenderRollingRate}%)을 초과할 수 없습니다.`);
                    }
                } else {
                    updatedList[idx] = { ...item, rollingRate: defaultRate };
                }
            }
        });
        setUserInfo({ ...userInfo, userRollingList: updatedList });
    };

    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 bg-gray-100 p-2">
                <span className="text-sm font-bold">{title} 전체 요율 적용</span>
                <input 
                    className="w-[50px] border px-1 text-sm"
                    type="text"
                    placeholder="요율"
                    value={defaultRate}
                    onChange={(e) => setDefaultRate(e.target.value)}
                />
                <span className="text-sm">%</span>
                <button 
                    className="px-2 py-1 bg-red-500 text-white text-xs rounded"
                    onClick={applyDefaultRate}
                >
                    적용
                </button>
            </div>
            <div className="grid grid-cols-4 gap-4">
                {userInfo.userRollingList
                    .filter(item => item.gameDiv === gameDiv)
                    .map((item, idx) => (
                        <RollingRateInput 
                            key={`rolling_${idx}`}
                            item={item}
                            onChange={handleRateChange}
                        />
                    ))}
            </div>
        </div>
    );
};

const UserDetail = (props) => {
    // localStorage에서 사용자 정보 직접 가져오기
    const user = getStorageWithExpiry('user');
    const [loading, setLoading] = useState(false)
    const [userInfo, setUserInfo] = useState({
        companyCode: "", companyType: "", parentId: "", userType: "6000",
        userId: "", userLevel: 1, userName: "", password: "", nickName: "", mobileNo: "", recommendCode: "",
        bankAccount: "", bankName: "", bankOwner: "",
        exchangePass: "", exchangeTime: 0,
        filler1: "",
        recommendYn: 1, bettingYn: 1, moneyRetYn: 1, pointRetYn: 1, transferPointYn: 1, blackYn: 0,
        status: 1, useYn: 1,
        upperUserList: [],
        userSportsSetting: {},
        userSportsPer: {},
        userRollingList: [],
        userCashSummary: {},
        gameBalance: null
    })

    const [sportsSetting, setSportsSetting] = useState({
        sportsYn: 1,
        casinoYn: 1,
        slotYn: 1,
        minigameYn: 1,
    })

    const [pointModal, setPointModal] = useState({visible: false, type: "0"})
    const [moneyModal, setMoneyModal] = useState({visible: false, type: "0"})
    const [messageModal, setMessageModal] = useState({visible: false})

    const [searchParam] = useSearchParams()
    const [userId, setUserId] = useState(searchParam.get("userId") ? searchParam.get("userId") : "");
    const procUserId = searchParam.get("procUserId") ? searchParam.get("procUserId") : ""

    const [sportDefaultRate, setSportDefaultRate] = useState("")
    const [minigameDefaultRate, setMinigameDefaultRate] = useState("")
    const [casinoDefaultRate, setCasinoDefaultRate] = useState("")
    const [slotDefaultRate, setSlotDefaultRate] = useState("")

    useEffect(() => {
        search()
    }, [userId])

    const search = () => {
        setLoading(true)

        // Redux store에서 현재 사용자의 companyCode 가져오기
        const currentUserCompanyCode = user?.companyCode;

        // companyCode가 있으면 게임 잔액 포함 조회, 없으면 기본 조회
        const apiCall = currentUserCompanyCode
            ? api.getGUserWithGameBalance(currentUserCompanyCode, userId)
            : api.getGUser(userId);

        apiCall.then(result => {
            const {data, status, statusText} = result;
            if (status === 200) {
                const userData = {
                    ...data,
                    // 기존 GUser 구조와 호환성을 위해 필요한 필드들 매핑
                    userName: data.userName,
                    mobileNo: data.mobileNo || "",
                    password: data.password || "",
                    exchangePass: data.exchangePass || "",
                    exchangeTime: data.exchangeTime || 0,
                    filler1: data.filler1 || "",
                    moneyRetYn: data.moneyRetYn,
                    pointRetYn: data.pointRetYn,
                    transferPointYn: data.transferPointYn,
                    blackYn: data.blackYn,
                    useYn: data.useYn,
                    offsetFeeRate: data.offsetFeeRate,
                    slotOffsetRate: data.slotOffsetRate,
                    gameBalance: data.gameBalance || null
                };

                setUserInfo(userData)

                if (data.userSportsSetting !== null) {
                    setSportsSetting(data.userSportsSetting)
                }
            }
        })
            .catch(ex => {
                window.alert(ex.message)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    // 게임사 보유머니 새로고침 함수
    const refreshGameBalance = async () => {
        if (!userInfo.userId || !userInfo.companyCode) {
            window.alert('사용자 정보가 없습니다.');
            return;
        }

        try {
            setLoading(true);

            // 게임사 보유머니 조회
            const balanceResponse = await api.getUnifiedBalance(userInfo.companyCode, userInfo.userId);

            if (balanceResponse.status === 200 && balanceResponse.data.success) {
                const gameBalance = balanceResponse.data.data;

                // userInfo의 gameBalance 업데이트
                setUserInfo(prev => ({
                    ...prev,
                    gameBalance: gameBalance
                }));

                console.log('게임사 보유머니 갱신 완료:', gameBalance);
            } else {
                window.alert('게임사 보유머니 조회에 실패했습니다.');
            }
        } catch (error) {
            console.error('게임사 보유머니 조회 오류:', error);
            window.alert('게임사 보유머니 조회 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    // 게임머니 가져오기 처리 함수
    const handleGameMoneyTransfer = async () => {
        if (!userInfo.userId || !userInfo.companyCode) {
            window.alert('사용자 정보가 없습니다.');
            return;
        }

        if (!window.confirm("게임머니를 가져오시겠어요?")) return;

        try {
            setLoading(true);

            // 1. 사용자의 게임머니 잔액 조회
            const balanceResponse = await api.getUnifiedBalance(userInfo.companyCode, userInfo.userId);

            if (balanceResponse.status === 200 && balanceResponse.data.success) {
                const totalBalance = balanceResponse.data.data.totalBalance || 0;

                if (totalBalance > 0) {
                    // 2. 통합 트랜스퍼 잔액 출금
                    const withdrawParams = {
                        companyCode: userInfo.companyCode,
                        userId: userInfo.userId,
                        userPw: userInfo.password // 사용자 비밀번호 사용
                    };

                    const withdrawResponse = await api.unifiedWithdraw(withdrawParams);

                    if (withdrawResponse.status === 200 && withdrawResponse.data.success) {
                        // 3. 성공 메시지 표시
                        window.alert(`잔액 ${numeral(totalBalance).format("0,0")} 원이 전환 처리되었습니다.`);

                        // 4. 사용자 정보 새로고침
                        search();
                    } else {
                        window.alert('게임머니 전환 처리에 실패했습니다.');
                    }
                } else {
                    window.alert('전환할 게임머니 잔액이 없습니다.');
                }
            } else {
                window.alert('게임머니 잔액 조회에 실패했습니다.');
            }
        } catch (error) {
            console.error('게임머니 전환 오류:', error);
            window.alert('게임머니 전환 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    }

    const getSportsBaseList = () => {
        api.getSportsBaseList().then(result => {
            const {data, status, statusText} = result
            if (status === 200) {
                setSportsSetting(data)
            }
        })
    }

    const handleUserInfo = (e) => {
        const {name, value} = e.target;
        setUserInfo({
            ...userInfo,
            [name]: value
        })
    }

    const handleUserGameSetting = (e) => {
        const {name, value} = e.target
    }

    const save = () => {
        if (window.confirm("저장하시겠어요?")) {
            setLoading(true)
            api.putGUser(userInfo).then(result => {
                const {data, status, statusText} = result;
                if (status === 200) {
                    window.alert("정상적으로 처리되었습니다.")
                }
            })
                .catch(ex => {
                    window.alert(ex.message)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    const saveSportsSetting = () => {
        if (window.confirm("저장하시겠어요?")) {
            const params = {
                seq: sportsSetting.seq ? sportsSetting.seq : -1,
                userId: userId,
                ...sportsSetting
            }

            setLoading(true)
            api.putSportsSetting(params).then(result => {
                const {data, status, statusText} = result;
                if (status === 200) {
                    window.alert("정상적으로 처리되었습니다.")
                    search();
                }
            })
                .catch(ex => {
                    window.alert(ex.message)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    const saveSportsPer = () => {
        if (window.confirm("저장하시겠어요?")) {
            setLoading(true)
            
            // 새로운 구조로 변환
            const params = {
                companyCode: userInfo.companyCode,
                userId: userInfo.userId,
                data: userInfo.userRollingList.map(item => ({
                    seq: item.seq,
                    gameDiv: item.gameDiv,
                    gameType: item.gameType,
                    rollingDiv: item.rollingDiv || "1",
                    rollingRate: item.rollingRate
                }))
            };
            
            api.putUserRolling(params).then(result => {
                const {data, status, statusText} = result;
                if (status === 200) {
                    window.alert(data.message)
                    search();
                }
            })
                .catch(ex => {
                    window.alert(ex.message)
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    const openPopup = (path) => {
        const params = "userId=" + userInfo.userId
        window.open('/#' + path + '/?' + params, "_blank", "width=1500, height=800");
    }

    const onButtonMenu = (value) => {
        switch (value) {
            case 0:
                setPointModal({visible: true, type: "0"})
                break;
            case 1:
                setPointModal({visible: true, type: "1"})
                break;
            case 2:
                setMoneyModal({visible: true, type: "0"})
                break;
            case 3:
                setMoneyModal({visible: true, type: "1"})
                break;
            case 4:
                openPopup('/betting/list')
                break;
            case 5:
                openPopup('/deposit')
                break;
            case 6:
                openPopup('/withdraw')
                break;
            case 7:
                openPopup('/point')
                break;
            case 8:
                openPopup('/user/login/history')
                break;
            case 9:
                setMessageModal({visible: true});
                break;
            case 10:
                openPopup('/message/list')
                break;
            case 11:
                openPopup('/partner/change')
                break;
            case 12:
                openPopup('/settle/user-list')
                break;
            case 13:
                openPopup('/user/history')
                break;
            case 14:
                handleGameMoneyTransfer();
                break;
        }
    }

    const procMoney = (money) => {
        const params = {
            companyCode: userInfo.companyCode,
            ...money,
            procUserId: procUserId
        }

        setLoading(true)
        api.postMoney(params).then(result => {
            const {data, status, statusText} = result
            if (status === 200) {
                window.alert("정상적으로 처리되었습니다.")
                search()
                return
            }
        })
            .catch(ex => {
                window.alert(ex.message)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const procPoint = (point) => {
        const params = {
            companyCode: userInfo.companyCode,
            ...point,
            procUser: procUserId
        }

        setLoading(true)
        api.postPoint(params).then(result => {
            const {data, status, statusText} = result
            if (status === 200) {
                window.alert("정상적으로 처리되었습니다.")
                search()
                return
            }
        })
            .catch(ex => {
                window.alert(ex.message)
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const procMessage = (message) => {
        const params = {
            companyCode: userInfo.companyCode,
            userId: userInfo.userId,
            subject: message.subject,
            contents: message.contents
        }

        setLoading(true)
        const headers = {
            "admin-id": procUserId
        }
        api.sendNote(params, headers).then(result => {
            const {status} = result
            if (status === 200) {
                window.alert("쪽지가 성공적으로 전송되었습니다.")
                return
            }
        })
        .catch(ex => {
            window.alert(ex.message)
        })
        .finally(() => {
            setLoading(false)
        })
    }

    return (
        <div className={"flex flex-col p-2 gap-1"}>
            {loading ? <Loading /> : null}
            <p className={"text-md font-bold"}>회원 관리 &gt; 회원 상세</p>
            <div className={"flex flex-row gap-1"}>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(0)}>포인트 지급</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(1)}>포인트 회수</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(2)}>보유금 지급</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(3)}>보유금 회수</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(4)}>배팅 내역</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(5)}>입금 내역</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(6)}>출금 내역</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(7)}>포인트 내역</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(8)}>접속 로그</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(9)}>쪽지 전송</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(10)}>쪽지 내역</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(11)}>파트너 변경</button>
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(12)}>일별 통계</button>
                {/*<button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(13)}>정보 수정 이력</button>*/}
                <button className={"text-sm bg-gray-800 text-white px-2"} onClick={() => onButtonMenu(14)}>게임머니 가져오기</button>
            </div>
            <div className={"border mt-1"}>
                <div className={"flex flex-row p-2 gap-2"}>
                    <span className={"text-sm font-bold"}>회원 아이디</span>
                    <input className={"text-sm border px-2"} value={userId} disabled={true}/>
                    <button className={"bg-gray-700 px-3 rounded text-white text-sm"} onClick={search}>조회</button>
                </div>
            </div>
            <div className={"flex flex-col gap-2"}>
                <div className={"flex flex-col"}>
                    <div className={"flex flex-row justify-between items-center bg-gray-100 p-2"}>
                        <span className={"text-md font-bold"}>유저정보</span>
                        <button className={"rounded text-xs text-white bg-gray-800 px-2 py-1"} onClick={save}>저장</button>
                    </div>
                    <div className="grid grid-cols-6 gap-x-4 gap-y-2 p-3 border">
                        {/* 첫 번째 행 */}
                        <InfoItem label="아이디">
                            <TextValue value={userInfo.userId} isBold={true} />
                        </InfoItem>
                        <InfoItem label="이름">
                            <InputField name="userName" value={userInfo.userName} onChange={handleUserInfo} />
                        </InfoItem>
                        <InfoItem label="닉네임">
                            <InputField name="nickName" value={userInfo.nickName} onChange={handleUserInfo} />
                        </InfoItem>

                        {/* 두 번째 행 */}
                        <InfoItem label="휴대폰번호">
                            <InputField name="mobileNo" value={userInfo.mobileNo} onChange={handleUserInfo} />
                        </InfoItem>
                        <InfoItem label="회원레벨">
                            <select name={"userLevel"} className={"w-full rounded border px-2 text-sm"} value={userInfo.userLevel} onChange={handleUserInfo}>
                                <option value={"1"}>1</option>
                                <option value={"2"}>2</option>
                                <option value={"3"}>3</option>
                                <option value={"4"}>4</option>
                                <option value={"5"}>5</option>
                                <option value={"6"}>6</option>
                            </select>
                        </InfoItem>
                        <InfoItem label="비밀번호">
                            <InputField name="password" value={userInfo.password} onChange={handleUserInfo} type="password" />
                        </InfoItem>

                        {/* 세 번째 행 */}
                        <InfoItem label="은행">
                            <InputField name={"bankName"} value={userInfo.bankName} onChange={handleUserInfo} />
                        </InfoItem>
                        <InfoItem label="계좌번호">
                            <InputField name={"bankAccount"} value={userInfo.bankAccount} onChange={handleUserInfo} />
                        </InfoItem>
                        <EmptyCell span={2} />

                        {/* 네 번째 행 */}
                        <InfoItem label="예금주">
                            <InputField name="bankOwner" value={userInfo.bankOwner} onChange={handleUserInfo} />
                        </InfoItem>
                        <InfoItem label="환전 비밀번호">
                            <InputField name="exchangePass" value={userInfo.exchangePass} onChange={handleUserInfo} type="password" />
                        </InfoItem>
                        <EmptyCell span={2} />

                        {/* 다섯 번째 행 */}
                        <InfoItem label="가입일">
                            <TextValue value={userInfo.instTime} />
                        </InfoItem>
                        <InfoItem label="가입코드">
                            <TextValue name="userId" value={userInfo.userId} onChange={handleUserInfo} />
                        </InfoItem>
                        <EmptyCell span={2} />

                        {/* --------------------------------------------- */}
                        <InfoItem label="보유머니">
                            <NumberValue value={userInfo.userCash} />
                        </InfoItem>
                        <InfoItem label="게임사보유머니">
                            <div className="flex items-center gap-2">
                                <NumberValue value={userInfo.gameBalance?.totalBalance || 0} />
                                <button
                                    className="p-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={refreshGameBalance}
                                    disabled={loading}
                                    title="게임사 보유머니 새로고침"
                                >
                                    <svg
                                        className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </InfoItem>
                        <InfoItem label="보유포인트">
                            <NumberValue value={userInfo.userPoint} />
                        </InfoItem>

                        {/* --------------------------------------------- */}
                        <InfoItem label="총 베팅액">
                            <NumberValue value={userInfo.userCashSummary?.totalBettingAmount || 0} />
                        </InfoItem>
                        <InfoItem label="총 당첨액">
                            <NumberValue value={userInfo.userCashSummary?.totalWinningAmount || 0} color={"text-green-500"} />
                        </InfoItem>
                        <InfoItem label="베팅/당첨 수익">
                            <NumberValue value={userInfo.userCashSummary?.totalBettingProfit || 0}
                                         color={userInfo.userCashSummary?.totalBettingProfit >= 0 ? "text-green-500" : "text-red-500"}/>
                        </InfoItem>

                        {/* --------------------------------------------- */}
                        <InfoItem label="입금액">
                            <NumberValue value={userInfo.userCashSummary?.totalDeposit || 0} color={"text-green-500"} />
                        </InfoItem>
                        <InfoItem label="출금액">
                            <NumberValue value={userInfo.userCashSummary?.totalWithdraw || 0} color={"text-red-500"} />
                        </InfoItem>
                        <InfoItem label="입금/출금 수익">
                            <NumberValue value={userInfo.userCashSummary?.totalCashProfit || 0}
                                         color={userInfo.userCashSummary?.totalCashProfit >= 0 ? "text-green-500" : "text-red-500"}/>
                        </InfoItem>

                        {/* --------------------------------------------- */}
                        <InfoItem label="당일 입금액">
                            <NumberValue value={userInfo.userCashSummary?.todayDeposit || 0} color={"text-green-500"} />
                        </InfoItem>
                        <InfoItem label="당일 출금액">
                            <NumberValue value={userInfo.userCashSummary?.todayWithdraw || 0} color={"text-red-500"} />
                        </InfoItem>
                        <EmptyCell span={2} />

                        {/* --------------------------------------------- */}
                        <InfoItem label="마지막 로그인">
                            <TextValue value={userInfo.lastLoginHistory ? userInfo.lastLoginHistory.instTime : ""} />
                        </InfoItem>
                        <InfoItem label="마지막 로그인 IP">
                            <TextValue value={userInfo.lastLoginHistory ? userInfo.lastLoginHistory.ip : ""} />
                        </InfoItem>
                        <EmptyCell span={2} />

                        {/* --------------------------------------------- */}
                        <InfoItem label="추천인 허용">
                            <ToggleSwitch
                                value={userInfo.recommendYn}
                                onChange={(value) => {
                                    setUserInfo({
                                        ...userInfo,
                                        recommendYn: convertBoolean(value)
                                    })
                                }}
                            />
                        </InfoItem>
                        <InfoItem label="추천인 보유 머니 지급 회수 권한">
                            <ToggleSwitch
                                value={userInfo.moneyRetYn}
                                onChange={(value) => {
                                    setUserInfo({
                                        ...userInfo,
                                        moneyRetYn: convertBoolean(value)
                                    })
                                }}
                            />
                        </InfoItem>
                        <InfoItem label="추천인 보유 포인트 지급 회수 권한">
                            <ToggleSwitch
                                value={userInfo.pointRetYn}
                                onChange={(value) => {
                                    setUserInfo({
                                        ...userInfo,
                                        pointRetYn: convertBoolean(value)
                                    })
                                }}
                                useResultBoolean={true}
                            />
                        </InfoItem>

                        {/* --------------------------------------------- */}
                        {/*<InfoItem label="정지 설정">*/}
                        {/*    <ToggleSwitch */}
                        {/*        value={userInfo.blackYn} */}
                        {/*        onChange={(value) => {*/}
                        {/*            setUserInfo({*/}
                        {/*                ...userInfo,*/}
                        {/*                blackYn: convertBoolean(value)*/}
                        {/*            })*/}
                        {/*        }}*/}
                        {/*        useResultBoolean={true}*/}
                        {/*    />*/}
                        {/*</InfoItem>*/}

                        {/* 아홉 번째 행 */}
                        <InfoItem label="정지 설정">
                            <ToggleSwitch
                                value={userInfo.status}
                                onChange={(value) => {
                                    setUserInfo({
                                        ...userInfo,
                                        status: convertBoolean(value)
                                    })
                                }}
                                useResultBoolean={true}
                            />
                        </InfoItem>
                        <InfoItem label="배팅 가능">
                            <ToggleSwitch
                                value={userInfo.bettingYn}
                                onChange={(value) => {
                                    setUserInfo({
                                        ...userInfo,
                                        bettingYn: convertBoolean(value)
                                    })
                                }}
                                useResultBoolean={true}
                            />
                        </InfoItem>
                        <InfoItem label="포인트 전환">
                            <ToggleSwitch
                                value={userInfo.transferPointYn}
                                onChange={(value) => {
                                    setUserInfo({
                                        ...userInfo,
                                        transferPointYn: convertBoolean(value)
                                    })
                                }}
                                useResultBoolean={true}
                            />
                        </InfoItem>
                        <InfoItem label="카지노 공배팅 확률">
                            <InputField name="offsetFeeRate" value={userInfo.offsetFeeRate} onChange={handleUserInfo} type="number" />
                        </InfoItem>
                        <InfoItem label="슬롯 공배팅 확률">
                            <InputField name="slotOffsetRate" value={userInfo.slotOffsetRate} onChange={handleUserInfo} type="number" />
                        </InfoItem>
                        <InfoItem label=""/>

                        {/* 열 번째 행 - 상위 파트너 리스트 */}
                        <div className="col-span-2 bg-gray-50 p-2 flex items-center">
                            <span className="text-sm font-bold">상위 파트너 리스트</span>
                        </div>
                        <div className="col-span-4 p-2 flex items-center">
                            {userInfo.upperUserList.length > 0 &&
                                <div className={"flex flex-row gap-1 items-center"}>
                                    {userInfo.upperUserList.map((item, idx) => (
                                        <span key={String(idx)}>
                                            <span className={"text-sm font-bold text-blue-500 cursor-pointer"} onClick={() => {
                                                setUserId(item)
                                            }}>{item}</span>
                                            {" >"}
                                        </span>
                                    ))}
                                </div>
                            }
                        </div>
                    </div>
                </div>
                <div className={"flex flex-col"}>
                    <div className={"flex flex-row justify-between items-center bg-gray-100 p-2"}>
                        <span className={"text-md font-bold"}>메모</span>
                    </div>
                    <div className={"flex h-[100px]"}>
                        <textarea name={"filler1"} className={"flex-1 p-1 border-1 text-sm"} value={userInfo.filler1} onChange={handleUserInfo} />
                    </div>
                </div>
                <div className={"flex flex-col"}>
                    <div className={"flex flex-row items-center gap-2"}>
                        <span className={"p-1 rounded-t border-t-2 border-x-2 border-indigo-500 text-sm font-bold"}>게임별 베팅설정</span>
                    </div>
                    <div className={"flex flex-row justify-between items-center bg-gray-100 p-2"}>
                        <span className={"text-md font-bold"}>게임목록</span>
                        <button className={"rounded text-xs text-white bg-gray-800 px-2 py-1"} onClick={saveSportsSetting}>저장</button>
                    </div>
                    <div className={"grid grid-cols-4 gap-2 p-2 border-1"}>
                        {isGameEnabled('sports', user?.companyCode) && (
                        <div className={"flex flex-row gap-2 items-center"}>
                            <span className={"w-[80px] text-sm font-bold"}>스포츠</span>
                            <ToggleSwitch
                                value={sportsSetting.sportsYn}
                                onChange={(value) => {
                                    setSportsSetting({
                                        ...sportsSetting,
                                        sportsYn: convertBoolean(value)
                                    })
                                }}
                            />
                        </div>
                        )}
                        {isGameEnabled('casino', user?.companyCode) && (
                        <div className={"flex flex-row gap-2 items-center"}>
                            <span className={"w-[80px] text-sm font-bold"}>카지노</span>
                            <ToggleSwitch
                                value={sportsSetting.casinoYn}
                                onChange={(value) => {
                                    setSportsSetting({
                                        ...sportsSetting,
                                        casinoYn: convertBoolean(value)
                                    })
                                }}
                            />
                        </div>
                        )}
                        {isGameEnabled('slot', user?.companyCode) && (
                        <div className={"flex flex-row gap-2 items-center"}>
                            <span className={"w-[80px] text-sm font-bold"}>슬롯</span>
                            <ToggleSwitch
                                value={sportsSetting.slotYn}
                                onChange={(value) => {
                                    setSportsSetting({
                                        ...sportsSetting,
                                        slotYn: convertBoolean(value)
                                    })
                                }}
                            />
                        </div>
                        )}
                        {isGameEnabled('minigame', user?.companyCode) && (
                        <div className={"flex flex-row gap-2 items-center"}>
                            <span className={"w-[80px] text-sm font-bold"}>미니게임</span>
                            <ToggleSwitch
                                value={sportsSetting.minigameYn}
                                onChange={(value) => {
                                    setSportsSetting({
                                        ...sportsSetting,
                                        minigameYn: convertBoolean(value)
                                    })
                                }}
                            />
                        </div>
                        )}
                    </div>
                </div>
                <div className={"flex flex-col"}>
                    <div className={"flex flex-row items-center gap-2"}>
                        <span className={"p-1 rounded-t border-t-2 border-x-2 border-indigo-500 text-sm font-bold"}>게임별 요율설정</span>
                    </div>
                    <div className={"flex flex-row justify-between items-center bg-gray-100 p-2"}>
                        <span className={"text-md font-bold"}>게임목록</span>
                        <button className={"rounded text-xs text-white bg-gray-800 px-2 py-1"} onClick={saveSportsPer}>저장</button>
                    </div>
                    <div className="flex flex-col gap-4">
                        {/* 스포츠 영역 */}
                        {isGameEnabled('sports', user?.companyCode) && (
                            <GameSection
                                title="스포츠"
                                gameDiv="sport"
                                defaultRate={sportDefaultRate}
                                setDefaultRate={setSportDefaultRate}
                                userInfo={userInfo}
                                setUserInfo={setUserInfo}
                            />
                        )}

                        {/* 미니게임 영역 */}
                        {isGameEnabled('minigame', user?.companyCode) && (
                            <GameSection
                                title="미니게임"
                                gameDiv="minigame"
                                defaultRate={minigameDefaultRate}
                                setDefaultRate={setMinigameDefaultRate}
                                userInfo={userInfo}
                                setUserInfo={setUserInfo}
                            />
                        )}

                        {/* 카지노 영역 */}
                        {isGameEnabled('casino', user?.companyCode) && (
                            <GameSection 
                                title="카지노"
                                gameDiv="casino"
                                defaultRate={casinoDefaultRate}
                                setDefaultRate={setCasinoDefaultRate}
                                userInfo={userInfo}
                                setUserInfo={setUserInfo}
                            />
                        )}

                        {/* 슬롯 영역 */}
                        {isGameEnabled('slot', user?.companyCode) && (
                            <GameSection 
                                title="슬롯"
                                gameDiv="slot"
                                defaultRate={slotDefaultRate}
                                setDefaultRate={setSlotDefaultRate}
                                userInfo={userInfo}
                                setUserInfo={setUserInfo}
                            />
                        )}
                    </div>
                </div>
            </div>

            <CModal backdrop={"static"}
                    alignment={"center"}
                    visible={pointModal.visible}
                    onClose={() => {
                        setPointModal({visible: false, type: "0"})}}>
                <UserPointModal user={userInfo}
                                type={pointModal.type}
                                proc={(point) => {
                                    procPoint(point)
                                }}
                                close={() => {
                                    setPointModal({visible: false, type: "0"})
                                    search()
                                }}
                />
            </CModal>

            <CModal backdrop={"static"}
                    alignment={"center"}
                    visible={moneyModal.visible}
                    onClose={() => {
                        setMoneyModal({visible: false, type: "0"})}}>
                <UserMoneyModal user={userInfo}
                                type={moneyModal.type}
                                proc={(money) => {
                                    procMoney(money)
                                }}
                                close={() => {
                                    setMoneyModal({visible: false, type: "0"})
                                    search()
                                }}
                />
            </CModal>

            <CModal backdrop={"static"}
                    alignment={"center"}
                    visible={messageModal.visible}
                    onClose={() => {
                        setMessageModal({visible: false})}}>
                <UserMessageModal user={userInfo}
                                  proc={(message) => {
                                      procMessage(message)
                                  }}
                                  close={() => {
                                      setMessageModal({visible: false})
                                      search()
                                  }}
                />
            </CModal>
        </div>
    )
}

const mapStateToProps = (state) => {
    const {account} = state;
    return {
        account
    }
}

export default connect(mapStateToProps, null)(UserDetail);